// Transactions Table - Replicating Sentry UI

import { Star, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { formatDuration, formatNumber, formatPercentage, getStatusFromScore, STATUS_CONFIG } from '@/types/performance';
import type { TransactionData } from '@/types/performance';

interface TransactionsTableProps {
  transactions: TransactionData[];
  isLoading?: boolean;
}

// Calculate performance score for a transaction based on p75 duration
function getTransactionScore(p75: number): number {
  // Good: < 1000ms, Poor: > 4000ms
  if (p75 <= 1000) return 100;
  if (p75 >= 4000) return 0;
  return Math.round(100 - ((p75 - 1000) / 3000) * 100);
}

export function TransactionsTable({ transactions, isLoading }: TransactionsTableProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Transactions</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8"></TableHead>
                <TableHead className="min-w-[200px]">Transaction</TableHead>
                <TableHead className="text-right">TPM</TableHead>
                <TableHead className="text-right">P50</TableHead>
                <TableHead className="text-right">P75</TableHead>
                <TableHead className="text-right">P95</TableHead>
                <TableHead className="text-right">Failure Rate</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Perf Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                    Nenhuma transação encontrada
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => {
                  const score = getTransactionScore(tx.p75);
                  const status = getStatusFromScore(score);
                  const statusConfig = STATUS_CONFIG[status];
                  
                  return (
                    <TableRow key={tx.transaction}>
                      <TableCell>
                        <Star className="h-4 w-4 text-muted-foreground/50 hover:text-yellow-500 cursor-pointer" />
                      </TableCell>
                      <TableCell className="font-mono text-sm truncate max-w-[250px]">
                        {tx.transaction}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {tx.tpm.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        {formatDuration(tx.p50)}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        {formatDuration(tx.p75)}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        {formatDuration(tx.p95)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span className={tx.failureRate > 0.05 ? 'text-red-500' : ''}>
                          {formatPercentage(tx.failureRate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {formatNumber(tx.users)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`text-xs px-2 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                          {statusConfig.label} {score}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
